import { cn } from '@e-kyc/utils';
import IMask, { type FactoryArg } from 'imask';
import { useEffect, useRef, useState } from 'react';
import { useController, type FieldValues, type UseControllerProps } from 'react-hook-form';

import IconButton from '../../icon-button';
import { VisibilityOffOutlinedIcon, VisibilityOutlinedIcon } from '../../icons';

type FormInputProps<TFieldValues extends FieldValues> = UseControllerProps<TFieldValues> &
  React.ComponentProps<'input'> & {
    label?: string;
    radios?: JSX.Element;
    required?: boolean;
    maskOptions?: FactoryArg;
    helperText?: string;
  };

// NOTE: every prop must be destructured here — `props` is spread onto the DOM <input> below,
// so anything left in it leaks out as an invalid attribute.
export function Input<TFieldValues extends FieldValues = FieldValues>({
  id,
  label,
  type = 'text',
  radios,
  placeholder = '',
  name,
  required,
  disabled,
  className,
  maskOptions,
  helperText,
  ...props
}: FormInputProps<TFieldValues>) {
  const {
    field,
    fieldState: { error },
  } = useController({ ...props, name, rules: { required, ...props.rules } });
  const [passwordVisible, setPasswordVisible] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const passwordFieldType = passwordVisible ? 'text' : 'password';

  const isRadio = type === 'radio';
  const Container = isRadio ? 'div' : 'label';
  const isError = props['aria-invalid'] === 'true';

  useEffect(() => {
    if (maskOptions && inputRef.current) {
      const maskedInput = IMask(inputRef.current, maskOptions);

      const handleAccept = () => {
        field.onChange(maskedInput.value);
      };

      maskedInput.on('accept', handleAccept);

      return () => {
        maskedInput.off('accept', handleAccept);
        maskedInput.destroy();
      };
    }
  }, [maskOptions, field]);

  return (
    <Container htmlFor={name}>
      {label ? (
        <div
          className={cn(
            'mb-2 text-start text-sm font-semibold',
            required && 'after:text-danger after:ml-1 after:content-["*"]',
          )}
        >
          {label}
        </div>
      ) : null}
      <div className={cn('relative', isRadio && 'grid gap-4')}>
        {!isRadio ? (
          <>
            <input
              {...field}
              id={id}
              ref={(el) => {
                field.ref(el);
                inputRef.current = el;
              }}
              type={type === 'password' ? passwordFieldType : type}
              placeholder={placeholder}
              disabled={disabled}
              className={cn(
                /*
              `outline-color` not working in Safari
              'px-4 py-3 w-full rounded-lg border border-[#0000001f] outline-primary',
              isError ? 'border-danger outline-danger' : '',
              */
                'focus:border-primary w-full rounded-md border-2 border-[#0000001f] px-4 py-2.5 outline-none',
                disabled && 'cursor-not-allowed bg-gray-100 text-gray-400',
                error || isError
                  ? 'border-danger focus:border-danger focus:shadow-as-border-danger'
                  : '',
                className,
              )}
              {...props}
            />
            {/* One slot: the error replaces the helper text. Mirrors Form.Select.
                `thai-wrap` (global.css) stops iOS Safari breaking mid-word inside a Thai
                run — see the note there. It must stay a plain class: `break-keep
                break-words` are one tailwind-merge conflict group, so cn() drops one. */}
            {error || helperText ? (
              <p
                className={cn('text-text-secondary thai-wrap mt-2 text-xs', error && 'text-danger')}
              >
                {error?.message ?? helperText}
              </p>
            ) : null}
            {type === 'password' && (
              <IconButton
                className='absolute top-1 right-1.5'
                onClick={() => setPasswordVisible((prev) => !prev)}
              >
                {passwordVisible ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
              </IconButton>
            )}
          </>
        ) : null}
        {radios}
      </div>
    </Container>
  );
}
